/**
 * RolesTab — read-only view of the canonical RBAC role → permission
 * matrix. Sprint P37o · Feb 2026.
 */
import { useQuery } from "@tanstack/react-query";
import { ShieldCheck, Warning } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export default function RolesTab() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["rbac", "roles"],
    queryFn: () => api.get("/rbac/roles").then((r) => r.data),
    staleTime: 60_000,
  });

  if (isLoading) return <TabSkeleton lines={12} />;
  if (error) return <TabError error={error} />;

  const roles = data?.roles || [];

  return (
    <div data-testid="rbac-roles-tab" className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading font-black text-2xl tracking-tight">Roles &amp; Permission Matrix</h2>
          <p className="mt-1 text-[12px] text-slate-500 max-w-3xl">
            The canonical role vocabulary and every permission granted at each role level.
            SUPER_ADMIN implicitly holds every permission. To modify individual users, use
            the <span className="text-brand-blue font-medium">Users</span> tab.
          </p>
        </div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500">
          {roles.length} roles
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="rbac-roles-table">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-slate-50 text-[10px] uppercase tracking-[0.15em] text-slate-500">
              <th className="text-left px-4 py-3 border-b border-slate-200 w-56">Role</th>
              <th className="text-left px-4 py-3 border-b border-slate-200" data-mobile-hide="sm">Description</th>
              <th className="text-right px-4 py-3 border-b border-slate-200 w-24">Perms</th>
            </tr>
          </thead>
          <tbody>
            {roles.map((r) => (
              <tr key={r.role} className="hover:bg-slate-50/70 align-top" data-testid={`role-row-${r.role}`}>
                <td className="px-4 py-3 border-b border-slate-100">
                  <div className="inline-flex items-center gap-2">
                    <ShieldCheck size={12} className="text-brand-blue" weight="fill" />
                    <span className="font-mono text-[12px] text-slate-800">{r.role}</span>
                  </div>
                </td>
                <td className="px-4 py-3 border-b border-slate-100" data-mobile-hide="sm">
                  <div className="text-slate-700 text-[12.5px]">{r.description}</div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {r.permissions.slice(0, 12).map((p) => (
                      <span key={p} className="text-[10px] font-mono px-2 py-0.5 rounded-sm bg-slate-100 border border-slate-200 text-slate-700">
                        {p}
                      </span>
                    ))}
                    {r.permissions.length > 12 && (
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded-sm text-slate-500">
                        +{r.permissions.length - 12} more
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 border-b border-slate-100 text-right">
                  <span className="text-slate-800 font-semibold">{r.permissions.length}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-slate-500">
        Roles are code-defined for auditability. Contact engineering to propose changes to the matrix.
      </p>
    </div>
  );
}

export function TabSkeleton({ lines = 8 }) {
  return (
    <div className="space-y-3 animate-pulse" data-testid="rbac-tab-loading">
      <div className="h-6 w-1/3 bg-slate-100 rounded" />
      <div className="h-4 w-2/3 bg-slate-100 rounded" />
      <div className="mt-4 space-y-2">
        {Array.from({ length: lines }).map((_, i) => (
          <div key={i} className="h-10 bg-slate-100 rounded" />
        ))}
      </div>
    </div>
  );
}

export function TabError({ error }) {
  const status = error?.response?.status;
  if (status === 403) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-[13px] text-amber-800" data-testid="rbac-tab-forbidden">
        <div className="inline-flex items-center gap-2 font-semibold">
          <Warning size={14} /> Access denied
        </div>
        <div className="mt-1">You do not have permission to view this section.</div>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 p-6 text-[13px] text-rose-800" data-testid="rbac-tab-error">
      <div className="inline-flex items-center gap-2 font-semibold">
        <Warning size={14} /> Failed to load
      </div>
      <div className="mt-1 text-[12px]">{error?.response?.data?.detail?.message || error.message}</div>
    </div>
  );
}
